import { memo } from 'react'

import { Close as CloseIcon } from '@mui/icons-material'
import { Box, DialogContent, DialogContentText, DialogTitle, IconButton, Typography } from '@mui/material'
import { AvcInstanceSdiAudioChannelUtilization, AvcInstanceSdiThumbnail } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useTranslation } from 'react-i18next'
import SimpleBar from 'simplebar-react'

import { CodeInline } from '@shared/components/MaterialUIEnhancements/CodeInline'
import { SdiDialogImage } from 'domains/ContentProcessing/AvcInstances/Instance/Dashboard/Sdi'

type SdiPanelDialogContentProps = {
  thumbnail?: AvcInstanceSdiThumbnail
  name: string
  height?: number
  width?: number
  fieldOrder?: string
  frameRate?: string | number
  id?: string
  sampleRate?: number
  channels?: AvcInstanceSdiAudioChannelUtilization[]
  isActive?: boolean
  onClose?: () => void
}

const TitleTextField = ({ title, text }: { title: string; text?: string | number }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
    <DialogContentText variant="heading-sm">{`${title}:`}</DialogContentText>
    <DialogContentText>{text || 'N/A'}</DialogContentText>
  </Box>
)

const SdiPanelDialogContentRaw = ({
  thumbnail,
  name,
  height,
  width,
  fieldOrder,
  frameRate,
  sampleRate,
  channels,
  id,
  isActive,
  onClose,
}: SdiPanelDialogContentProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const { enqueueSnackbar } = useSnackbar()

  return (
    <DialogContent>
      <Box component={SimpleBar} sx={{ maxHeight: 600, maxWidth: 480 }}>
        <DialogTitle sx={{ padding: 0 }}>{name}</DialogTitle>
        <IconButton sx={{ position: 'absolute', top: 0, right: 0 }} onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
        <CopyToClipboard
          text={id || ''}
          onCopy={() => {
            enqueueSnackbar(t('copiedValueToClipboard', { value: 'ID' }), { variant: 'info' })
          }}
        >
          <CodeInline sx={{ mb: 2, cursor: 'pointer' }}>{id}</CodeInline>
        </CopyToClipboard>
        <SdiDialogImage {...thumbnail} />
        {isActive ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <DialogContentText variant="heading-base" sx={{ mb: 1 }}>
              {t('domain:ContentProcessing.Instance.videoInfo')}:
            </DialogContentText>

            <TitleTextField
              title={t('domain:ContentProcessing.Instance.resolution')}
              text={`${width || 'N/A'}x${height || 'N/A'}`}
            />
            <TitleTextField title={t('domain:ContentProcessing.Instance.frameRate')} text={frameRate} />
            <TitleTextField title={t('domain:ContentProcessing.Instance.fieldOrder')} text={fieldOrder} />

            <DialogContentText variant="heading-base" sx={{ mb: 1, mt: 2 }}>
              {t('domain:ContentProcessing.Instance.audioInfo')}:
            </DialogContentText>

            <TitleTextField title={t('domain:ContentProcessing.Instance.sampleRate')} text={sampleRate} />
            <TitleTextField
              title={t('domain:ContentProcessing.Instance.volumes')}
              text={
                channels && channels.length
                  ? channels
                      .map((c) => (c.volume ? `(${c.index}): ${Math.round(c.volume * 10) / 10}dB` : null))
                      .join(', ')
                  : undefined
              }
            />
          </Box>
        ) : (
          <Typography sx={{ textTransform: 'uppercase' }} variant="text-lg">
            {t('domain:ContentProcessing.Instance.noSignal')}
          </Typography>
        )}
      </Box>
    </DialogContent>
  )
}

export const SdiPanelDialogContent = memo(SdiPanelDialogContentRaw)
